import { Combobox, Select, Badge } from '@cinesend/ui-kit'
import { useQuery } from '@tanstack/react-query'

const parseValues = (value, isMulti) => {
  if (!value) return []
  if (!isMulti) return [value]
  try {
    const parsed = JSON.parse(value)
    // A non-array value is probably a "bad" value from a previous import. We'll just display it as is
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return [value]
  }
}

const withUnknownValues = (options, selected) => [
  ...options,
  ...selected
    .filter((item) => !options.some((opt) => String(opt.value) === String(item)))
    .map((item) => ({ value: item, label: String(item) })),
]

const toKitOptions = (options) => options.map((opt) => ({ ...opt, value: String(opt.value) }))

const CollectionDropdown = ({ field, label, value, isLoading, languageID = null, saveField, cycleID, error }) => {
  const { data, isLoading: isLoadingOptions } = useQuery([
    `/api/collection-list/${field.collection_id}/${cycleID}/${languageID ?? ''}`,
  ])
  const selected = parseValues(value, field.is_multi_select)
  const options = withUnknownValues(
    data ? data.map((option) => ({ value: option.id, label: option.label })) : [],
    selected
  )
  const originalValue = (id) => options.find((opt) => String(opt.value) === id).value
  const commonProps = {
    label: label ?? field.name,
    options: toKitOptions(options),
    disabled: field?.is_external_api_value_source || isLoading || isLoadingOptions,
    error,
  }

  if (field.is_multi_select) {
    return (
      <Combobox
        {...commonProps}
        multiple
        value={selected.map(String)}
        onValueChange={(ids) => saveField(JSON.stringify(ids.map(originalValue)))}
      />
    )
  }

  return (
    <Select
      fullWidth
      {...commonProps}
      value={selected.length ? String(selected[0]) : null}
      onValueChange={(id) => saveField(originalValue(id))}
    />
  )
}

const LanguageDropdown = ({ field, label, value, isLoading, saveField, error }) => {
  const { data, isLoading: isLoadingOptions } = useQuery(['/api/languages'])
  const options = data ? data.map((option) => ({ value: option.id, label: option.eng_description })) : []
  return (
    <Combobox
      label={label ?? field.name}
      disabled={field?.is_external_api_value_source || isLoading || isLoadingOptions}
      clearable
      value={value ? String(JSON.parse(value)) : null}
      options={toKitOptions(options)}
      error={error}
      onValueChange={(id) => saveField(id ? options.find((opt) => String(opt.value) === id).value : null)}
    />
  )
}

const Dropdown = ({ field, ...props }) => {
  if (field.collection_id && !field.use_languages) {
    return <CollectionDropdown field={field} {...props} />
  } else if (field.use_languages) {
    return <LanguageDropdown field={field} {...props} />
  }
  return <Badge tone='error'>Undefined Dropdown</Badge>
}

export default Dropdown
